import { ISaSmsPlatformSettings } from '@interfaces/sa-sms';
import {
    Alert,
    Button,
    Card,
    Col,
    Form,
    InputNumber,
    notification,
    Row,
    Space,
    Switch,
    Typography,
} from 'antd';
import { useSaSmsWrite } from 'hooks/use-sa-sms-api';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SaSmsReasonModal } from './sa-sms-reason-modal';

type Props = { platform: ISaSmsPlatformSettings; canEdit: boolean; onChanged: () => void };
type Values = Pick<
    ISaSmsPlatformSettings,
    'tierDefaults' | 'abuseThresholds' | 'backlogDays' | 'mmsCredits'
>;
const TIERS = ['new', 'standard'] as const;

/** Tier defaults, abuse thresholds, backlog cap and the platform-wide "pause all outbound" (each save asks for a reason). */
export const SmsPlatformSettingsForm: React.FC<Props> = ({ platform, canEdit, onChanged }) => {
    const { t } = useTranslation(['sa-sms']);
    const { send } = useSaSmsWrite();
    const [editing, setEditing] = useState(false);
    const [pausing, setPausing] = useState<boolean | null>(null);

    const save = async ({ reason, ...values }: Values & { reason: string }) => {
        await send('put', '/settings/platform', { ...values, reason });
        notification.success({ message: t('saved') });
        setEditing(false);
        onChanged();
    };
    const pause = async ({ reason }: { reason: string }) => {
        await send('put', '/settings/platform', { outboundPausedAll: pausing, reason });
        notification.success({ message: t('saved') });
        setPausing(null);
        onChanged();
    };

    return (
        <Card
            title={t('settings.platform')}
            extra={canEdit && <Button onClick={() => setEditing(true)}>{t('actions.edit')}</Button>}
        >
            {platform.outboundPausedAll && (
                <Alert
                    type="error"
                    showIcon
                    message={t('settings.pausedAllBanner')}
                    style={{ marginBottom: 16 }}
                />
            )}
            <Space style={{ marginBottom: 16 }}>
                <Switch
                    checked={platform.outboundPausedAll}
                    disabled={!canEdit}
                    onChange={(v) => setPausing(v)}
                />
                <Typography.Text strong>{t('settings.pauseAll')}</Typography.Text>
            </Space>
            <Row gutter={16}>
                {TIERS.map((tier) => (
                    <Col key={tier} xs={24} md={12}>
                        <Typography.Text strong>{t(`tier.${tier}`)}</Typography.Text>
                        <p>
                            {t('settings.tierLine', {
                                campaign: platform.tierDefaults[tier]?.campaign,
                                daily: platform.tierDefaults[tier]?.daily,
                            })}
                        </p>
                    </Col>
                ))}
            </Row>
            <p>{t('settings.backlog', { days: platform.backlogDays })}</p>
            <p>{t('settings.mmsCredits', { credits: platform.mmsCredits })}</p>
            {Object.entries(platform.abuseThresholds).map(([group, knobs]) => (
                <p key={group}>
                    <Typography.Text strong>
                        {t(`settings.abuse.${group}`, { defaultValue: group })}:{' '}
                    </Typography.Text>
                    {Object.entries(knobs)
                        .map(([k, v]) => `${k}=${String(v)}`)
                        .join(', ')}
                </p>
            ))}
            <SaSmsReasonModal<Values>
                open={editing}
                title={t('settings.platform')}
                initialValues={{
                    tierDefaults: platform.tierDefaults,
                    abuseThresholds: platform.abuseThresholds,
                    backlogDays: platform.backlogDays,
                    mmsCredits: platform.mmsCredits,
                }}
                onCancel={() => setEditing(false)}
                onConfirm={save}
            >
                {TIERS.map((tier) => (
                    <Space key={tier} align="start" wrap>
                        <Form.Item
                            name={['tierDefaults', tier, 'campaign']}
                            label={`${t(`tier.${tier}`)} - ${t('limits.campaign')}`}
                            rules={[{ required: true }]}
                        >
                            <InputNumber min={1} precision={0} />
                        </Form.Item>
                        <Form.Item
                            name={['tierDefaults', tier, 'daily']}
                            label={t('limits.daily')}
                            rules={[{ required: true }]}
                        >
                            <InputNumber min={1} precision={0} />
                        </Form.Item>
                    </Space>
                ))}
                <Form.Item
                    name="backlogDays"
                    label={t('limits.backlog')}
                    rules={[{ required: true }]}
                >
                    <InputNumber min={1} max={365} precision={0} />
                </Form.Item>
                <Form.Item
                    name="mmsCredits"
                    label={t('settings.mmsCreditsLabel')}
                    extra={t('settings.mmsCreditsHelp')}
                    rules={[{ required: true }]}
                >
                    <InputNumber min={1} max={50} precision={0} />
                </Form.Item>
                {Object.entries(platform.abuseThresholds).map(([group, knobs]) => (
                    <div key={group}>
                        <Typography.Text strong>
                            {t(`settings.abuse.${group}`, { defaultValue: group })}
                        </Typography.Text>
                        <Space wrap align="start">
                            {Object.entries(knobs).map(([knob, value]) => (
                                <Form.Item
                                    key={knob}
                                    name={['abuseThresholds', group, knob]}
                                    label={knob}
                                    valuePropName={typeof value === 'boolean' ? 'checked' : 'value'}
                                >
                                    {typeof value === 'boolean' ? (
                                        <Switch />
                                    ) : (
                                        <InputNumber min={0} />
                                    )}
                                </Form.Item>
                            ))}
                        </Space>
                    </div>
                ))}
            </SaSmsReasonModal>
            <SaSmsReasonModal
                open={pausing !== null}
                danger
                title={pausing ? t('settings.pauseAllConfirm') : t('settings.resumeAllConfirm')}
                warning={pausing ? t('settings.pauseAllWarning') : undefined}
                onCancel={() => setPausing(null)}
                onConfirm={pause}
            />
        </Card>
    );
};
